/**
 * How the set table PAINTS a column, once the shared rule has decided which columns there are.
 *
 * The decision itself is not here and must not be: `planBuilderColumns` in `~/shared/measurement`
 * answers "which column". What lives here is the rendering — each column's i18n KEY, the
 * `SetDraft.prescribed*` number its cell edits, and how wide it sits.
 *
 * ── Two exceptions, and both are deliberate ──────────────────────────────────────────────────
 * **Weight is the one column that is not one-to-one with a field.** Its header and its underlying
 * key switch on the exercise's `prescriptionMode`: `AbsoluteWeight` writes `prescribedWeight` under
 * a "Ciężar" header, `PercentOfOneRm` writes `prescribedPercent` under "% 1RM". That is why
 * `resolveValueKey` exists rather than every caller reading `field.valueKey` directly.
 *
 * **Rest is not a measurement field.** No exercise definition declares it and the dictionary does
 * not hold it, so it reaches the grid through its own flag (`ExerciseDraft.showsRestColumn`) and
 * its own rule (`dayRest.ts`) rather than through the measurement contract. The canon paints it as
 * a trailing, quieter cell rather than a uniform peer of the metrics.
 *
 * This module holds i18n KEYS, never `t` and never resolved text — `model/` may not import i18next,
 * and a key is data.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`.
 */

import type { MeasurementFieldKey } from '~/shared/measurement';

import type { SetDraft } from './planDraft.types';

/** Every rendered set-table column: a measurement field, or the non-measured Rest column. */
export type SetGridFieldKey = MeasurementFieldKey | 'rest';

/** The numeric `SetDraft.prescribed*` key a column reads and writes. */
export type SetValueKey =
  | 'prescribedWeight'
  | 'prescribedReps'
  | 'prescribedDurationSeconds'
  | 'prescribedDistanceMeters'
  | 'prescribedRpe'
  | 'prescribedRir'
  | 'prescribedRounds'
  | 'prescribedRestTime'
  | 'prescribedPercent';

/**
 * How a column is painted. `metric` is the uniform cell every measurement column uses; `secondary`
 * is the quieter trailing cell REST keeps.
 */
export type SetGridColumnEmphasis = 'metric' | 'secondary';

export interface SetGridField {
  readonly key: SetGridFieldKey;
  /** The `SetDraft` numeric field this column edits. Weight resolves to percent in %-of-1RM mode. */
  readonly valueKey: SetValueKey;
  /** i18n key for the column header — CHROME, so it is translated by whoever renders it. */
  readonly labelKey: string;
  readonly emphasis: SetGridColumnEmphasis;
}

const LABEL = 'web:planBuilder.day.field';

/**
 * How each column is painted, EXHAUSTIVE over the field keys, so a field added to the dictionary
 * breaks the build here rather than going quietly missing from the table.
 *
 * `null` marks a field with no cell to paint, and it is the same two `hasWireColumn` withholds.
 * `hasWireColumn` is the authority the column rules read; this map only has to agree with it — a
 * `null` here against a wire column there would be a column that renders nothing.
 */
const FIELD_META: Record<SetGridFieldKey, SetGridField | null> = {
  weight: { key: 'weight', valueKey: 'prescribedWeight', labelKey: `${LABEL}.weight`, emphasis: 'metric' },
  reps: { key: 'reps', valueKey: 'prescribedReps', labelKey: `${LABEL}.reps`, emphasis: 'metric' },
  duration: {
    key: 'duration',
    valueKey: 'prescribedDurationSeconds',
    labelKey: `${LABEL}.duration`,
    emphasis: 'metric',
  },
  distance: {
    key: 'distance',
    valueKey: 'prescribedDistanceMeters',
    labelKey: `${LABEL}.distance`,
    emphasis: 'metric',
  },
  rounds: { key: 'rounds', valueKey: 'prescribedRounds', labelKey: `${LABEL}.rounds`, emphasis: 'metric' },
  rpe: { key: 'rpe', valueKey: 'prescribedRpe', labelKey: `${LABEL}.rpe`, emphasis: 'metric' },
  rir: { key: 'rir', valueKey: 'prescribedRir', labelKey: `${LABEL}.rir`, emphasis: 'metric' },
  speed: null,
  tempo: null,
  rest: { key: 'rest', valueKey: 'prescribedRestTime', labelKey: `${LABEL}.rest`, emphasis: 'secondary' },
};

/** The `SetDraft` numeric key a measurement column writes — the input to clearing a column. */
export function measurementValueKey(field: MeasurementFieldKey): SetValueKey | null {
  return FIELD_META[field]?.valueKey ?? null;
}

/** True when the exercise's DEFINITION measures the given field. */
export function hasMeasurementField(
  measurementFields: readonly MeasurementFieldKey[],
  key: MeasurementFieldKey,
): boolean {
  return measurementFields.includes(key);
}

/**
 * True when the exercise measures load. **This is the gate on the kg ↔ %1RM toggle** — an exercise
 * that does not measure weight has no load to prescribe either way, so offering the switch would
 * offer a choice between two things it cannot do.
 */
export function hasWeightField(measurementFields: readonly MeasurementFieldKey[]): boolean {
  return hasMeasurementField(measurementFields, 'weight');
}

/** A rendered table column. */
export interface ResolvedGridColumn {
  readonly field: SetGridField;
  /** The measurement field the column stands for — `null` for REST, which is not one. */
  readonly measurementField: MeasurementFieldKey | null;
}

/**
 * Paint the exercise's visible columns: the measurement columns the shared rule returned, **in the
 * order it returned them**, then REST trailing when its own rule shows it.
 *
 * Nothing is sorted here. `planBuilderColumns` already hands them over in the ONE order this app
 * renders — `renderableColumns`' — and a second sort on this surface is precisely the divergence
 * that made an exercise read "REPS · KG · RPE" when prescribed and "KG × REPS · RPE" when reviewed
 * on the phone. That order is the dictionary's with REPS ahead of WEIGHT, on the 02.08 gallery's
 * authority; `renderableColumns` states the swap and its cost.
 */
export function resolveGridColumns(
  visibleFields: readonly MeasurementFieldKey[],
  showsRestColumn: boolean,
): ResolvedGridColumn[] {
  const columns: ResolvedGridColumn[] = [];
  for (const field of visibleFields) {
    const meta = FIELD_META[field];
    if (meta !== null) columns.push({ field: meta, measurementField: field });
  }
  const rest = FIELD_META.rest;
  if (showsRestColumn && rest !== null) columns.push({ field: rest, measurementField: null });
  return columns;
}

/**
 * The numeric key a WEIGHT column edits, given the prescription mode. Every other column always
 * uses its own `valueKey`.
 */
export function resolveValueKey(field: SetGridField, isPercentMode: boolean): SetValueKey {
  if (field.key === 'weight' && isPercentMode) return 'prescribedPercent';
  return field.valueKey;
}

/** Read a column's current value off a set. `null` for an empty cell — never `0`, never `''`. */
export function readCellValue(set: SetDraft, valueKey: SetValueKey): number | null {
  return set[valueKey] ?? null;
}
